import { API_URL, createHeaders } from './base'
import { localDayBoundsUTC, toLocalDateString } from '@/shared/lib/formatters'

export interface ActivityItem {
  type: 'execution_entry' | 'workflow_update'
  id: string
  timestamp: string
  workflow_id: number
  action: string
  agent: string | null
  summary: string
  details: Record<string, unknown> | null
}

export async function getActivityFeed(since?: string): Promise<ActivityItem[]> {
  const params = new URLSearchParams({ limit: '50' })
  if (since) params.set('since', since)
  const response = await fetch(`${API_URL}/activity?${params}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch activity feed')
  return response.json()
}

export interface ActiveEnrichment {
  workflow_id: number
  title: string
  agent: string | null
}

export interface PipelineStatus {
  active_enrichments: ActiveEnrichment[]
  orchestrator_active: boolean
}

export async function getPipelineStatus(): Promise<PipelineStatus> {
  const response = await fetch(`${API_URL}/activity/pipeline-status`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch pipeline status')
  return response.json()
}

// Personal activity for year graph
export interface PersonalActivityDay {
  date: string           // YYYY-MM-DD
  count: number
  tasks: number
  habits: number
  deep_work_sessions: number
  deep_work_minutes: number
}

export async function getPersonalActivity(since: string, until?: string): Promise<PersonalActivityDay[]> {
  // Convert local date strings to UTC boundaries so the backend filters by the
  // user's actual local day rather than UTC midnight.
  const { since_utc } = localDayBoundsUTC(since)
  const effectiveUntil = until ?? toLocalDateString(new Date())
  const { until_utc } = localDayBoundsUTC(effectiveUntil)
  const params = new URLSearchParams({ since_utc, until_utc })
  const response = await fetch(`${API_URL}/activity/personal?${params}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch personal activity')
  return response.json()
}

// Full detail for a single day
export interface DayActivity {
  date: string
  tasks: Array<{ id: number; title: string; completed_at: string }>
  habits: Array<{ habit_id: number; name: string; completion_date: string; completed_value: number | null }>
  deep_work: Array<{ id: number; start_time: string; end_time: string | null; duration_minutes: number | null; notes: string | null }>
  calendar_events: Array<{ id: string; summary: string; start_time: string; end_time: string | null; location: string | null }>
}

export async function getDayActivity(date: string): Promise<DayActivity> {
  // Keep `date` for habit queries (stored as local date). Add UTC bounds for
  // task and deep work queries so items at 11pm local are included correctly.
  const { since_utc, until_utc } = localDayBoundsUTC(date)
  const params = new URLSearchParams({ date, since_utc, until_utc })
  const response = await fetch(`${API_URL}/activity/day?${params}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch day activity')
  return response.json()
}
